import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';

/**
 * Hook to set up real-time profile updates that invalidate relevant queries
 * throughout the app when profile data changes
 */
export const useRealtimeProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    console.log('🔗 Setting up real-time profile updates for user:', user.id);

    const channel = supabase
      .channel('profile-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          console.log('📡 REAL-TIME: Profile updated payload:', payload);
          console.log('📡 REAL-TIME: Event type:', payload.eventType);
          console.log('📡 REAL-TIME: Old data:', payload.old);
          console.log('📡 REAL-TIME: New data:', payload.new);
          
          const updatedProfile = payload.new;
          const oldProfile = payload.old;
          
          // Invalidate all relevant queries to force refresh across the app
          const queriesToInvalidate = [
            'instructor-students',
            'student-profiles',
            'lessons',
            'calendar-events',
            'student-progress',
            'instructor-dashboard'
          ];

          queriesToInvalidate.forEach(queryKey => {
            queryClient.invalidateQueries({ queryKey: [queryKey] });
          });

          // Also invalidate specific user profile queries
          queryClient.invalidateQueries({ 
            queryKey: ['profile', updatedProfile.user_id] 
          });

          console.log('🔄 Invalidated queries for profile update');
        }
      )
      .subscribe((status) => {
        console.log('📡 Profile real-time subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up profile real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
};